/**
 * ops API クライアント。`/ops/requests`（一覧・ページング）と `/ops/requests/status-counts`
 * （ステート別件数）を叩く。フック（useOps）からのみ利用し、直接 fetch はしない（_request 経由）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼の一覧（offset ページング）とステート別件数を取得する API クライアント
 * @graph-connects /ops/requests [calls] ops 依頼一覧の取得
 * @graph-connects /ops/requests/status-counts [calls] ステート別件数の取得
 */
import type {
  OpsAssignment,
  OpsCover,
  OpsListScope,
  OpsRequestDraft,
  OpsRotationCadence,
  OpsSendBackReason,
  OpsSimilarBug,
  OpsSimilarItem,
  OpsSimilarResult,
  OpsSloGranularity,
  OpsSloTrendMonths,
  OpsStateHistoryEvent,
} from '@cortex/ops-core';
import { config } from '../../config.js';
import { ApiError, request } from './_request.js';
import { dedupeOpsRequestsById } from '../ops-request-pages.js';
import type {
  CreateOpsBugTicketInput,
  CreatedOpsBugTicket,
  OpsBugTicketDraft,
  OpsCategory,
  OpsLinkableRelatedTicketKind,
  OpsRequest,
  OpsService,
  OpsState,
  OpsUrgency,
} from '../../types/ops.js';

// 類似スキャン結果の型（OpsSimilarItem / OpsSimilarBug / OpsSimilarResult）は @cortex/ops-core へ移動した
// ため、ここでは再宣言せず ops-core を単一 source として再エクスポートする（product-api と drift させない）。
export type { OpsSimilarBug, OpsSimilarItem, OpsSimilarResult };

/** 割当更新の入力（service はパス、updatedBy はサーバーが認証セッションから付与）。 @graph-connects none */
export interface OpsAssignmentEdit {
  /** オペ担ローテーション queue（nickName）。 */
  opetanQueue: string[];
  /** オペ担の頻度。 */
  opetanCadence: OpsRotationCadence;
  /** review_sql 当番ローテーション queue（nickName）。 */
  reviewerQueue: string[];
  /** review_sql の頻度。 */
  reviewerCadence: OpsRotationCadence;
  /** 日付指定のカバー（当日=シフト代理 / 将来日=休日オペたん。該当日はローテーションより優先）。空配列 = なし。 */
  covers: OpsCover[];
}

/** 記録済みロスター変更 1 件（History の実績表示用。新しい順で取得）。 @graph-connects none */
export interface OpsAssignmentEvent {
  /** 対象サービス slug。 */
  service: OpsService;
  /** オペ担ローテーション queue。 */
  opetanQueue: string[];
  /** オペ担の頻度。 */
  opetanCadence: OpsRotationCadence;
  /** review_sql 当番ローテーション queue。 */
  reviewerQueue: string[];
  /** review_sql の頻度。 */
  reviewerCadence: OpsRotationCadence;
  /** 日付指定のカバー（当日=シフト代理 / 将来日=休日オペたん）。空配列 = なし。 */
  covers: OpsCover[];
  /** 変更者の nickName。 */
  updatedBy: string;
  /** 変更が有効になった時刻（ISO 8601）。 */
  effectiveFrom: string;
}

/** ステータス絞り込み（all = 全件）。 @graph-connects none */
export type OpsStatusFilter = OpsState | 'all';

/** サービス絞り込み（all = 全サービス）。 @graph-connects none */
export type OpsServiceFilter = OpsService | 'all';

/** 一覧の 1 ページ応答。 @graph-connects none */
export interface OpsRequestsPage {
  /** この頁の依頼。 */
  items: OpsRequest[];
  /** 次頁の offset（無ければ null）。 */
  nextOffset: number | null;
  /** 絞り込み後の総件数。 */
  total: number;
}

/** ステート別件数。 @graph-connects none */
export interface OpsStatusCounts {
  /** 全件。 */
  all: number;
  /** open 件数。 */
  open: number;
  /** investigating 件数。 */
  investigating: number;
  /** blocked 件数。 */
  blocked: number;
  /** closed 件数。 */
  closed: number;
}

/**
 * 依頼内容編集の入力（PATCH /content）。全フィールド任意で、指定したフィールドのみ更新する。
 * wfid / csTicketUrl は null / 空文字で消去できる（後から WFID を追加 / クリアするフローに対応）。
 * userIds は起票後の対象ユーザー追加・訂正に使い、[] で対象ユーザーなし。
 * @graph-connects none
 */
export interface OpsContentEdit {
  /** 背景（5W）。指定時のみ更新。 */
  background?: string;
  /** 対応してほしい内容。指定時のみ更新。 */
  requested?: string;
  /** WFID（ワークフロー ID）。null / 空文字で消去。指定時のみ更新。 */
  wfid?: string | null;
  /** CS チケット URL（http(s)）。null / 空文字で消去。指定時のみ更新。 */
  csTicketUrl?: string | null;
  /** 依頼種別（誤った種別での起票を訂正する）。指定時のみ更新。 */
  category?: OpsCategory;
  /** 対象サービス（誤ったサービスでの起票を訂正する）。指定時のみ更新。 */
  service?: OpsService;
  /** 対象ユーザー ID（数字のみ・複数可）。[] で対象ユーザーなし。指定時のみ更新。 */
  userIds?: string[];
}

/**
 * 関連チケット紐付けの入力（POST /related-tickets、および起票時 linkedTickets）。linkedBy / linkedAt は
 * サーバーが付与する。
 * @graph-connects none
 */
export interface OpsRelatedTicketInput {
  /** 参照先種別（ops 依頼 / Backlog）。 */
  target: 'ops' | 'backlog';
  /** 関係種別（本件調査結果バグ / 参考バグ / 参考 ops。補足対応の対リンクは人手で張れない）。 */
  kind: OpsLinkableRelatedTicketKind;
  /** 参照キー（ops は id、backlog は issueKey）。 */
  ref: string;
  /** 表示 URL（任意）。 */
  url?: string | null;
  /** 1 行タイトル / 概要のスナップショット（任意）。 */
  title?: string | null;
}

/**
 * 新規依頼作成の追加入力（起票時の事前スキャン結果 + 選択した初期関連チケット）。いずれも任意で、
 * 後方互換のため省略できる。
 * @graph-connects none
 */
export interface OpsCreateRequestExtras {
  /** 起票時点の事前スキャン結果（scannedAt はサーバーが付与）。 */
  preScan?: OpsSimilarResult;
  /** 起票時に紐付ける初期関連チケット。 */
  linkedTickets?: OpsRelatedTicketInput[];
  /** 補足対応として起票するときの元依頼 id（完了済みのダッシュボード起票の依頼に限る）。 */
  followUpOf?: string;
}

/**
 * API レスポンスの ops 依頼を正規化する。想定外に `userIds` / `attachments` 配列が欠けたレスポンス
 * （一覧経路・デプロイ skew で新フィールド未提供など）でも、フロントが常に読む配列を必ず配列で埋める（防御的読取）。
 * @graph-connects none
 */
function normalizeOpsRequest(r: OpsRequest): OpsRequest {
  const hasUserIds = Array.isArray(r.userIds);
  const hasAttachments = Array.isArray(r.attachments);
  if (hasUserIds && hasAttachments) return r;
  return {
    ...r,
    userIds: hasUserIds ? r.userIds : [],
    attachments: hasAttachments ? r.attachments : [],
  };
}

/**
 * 添付ファイルの実体（バイト列）を配信するプロキシ URL を組み立てる。`<img src>` / ダウンロードリンクは
 * この URL を指す（API クライアントの request() を通さず、ブラウザが Cookie 認証で直接取得する）。バケット
 * URL ではなくプロキシ経由なので、URL 自体は機密を含まない。**base は必ず `config.apiUrl` を使う**こと：
 * 本番では web (product.air-closet.ai) と API (api-product.air-closet.ai) が別オリジンで、`/api` は web 側に
 * proxy されないため、相対 `/api/...` だと web オリジンの SPA HTML が返り画像/PDF が壊れる。request() と同じ
 * base を使えば dev（`/api` → Vite proxy）でも prod（API オリジン絶対 URL、Cookie は same-site subdomain で送出）
 * でも正しく実体に到達する。
 * @graph-connects none
 */
export function opsAttachmentContentUrl(opsId: string, attachmentId: string): string {
  return `${config.apiUrl}/ops/requests/${encodeURIComponent(opsId)}/attachments/${encodeURIComponent(
    attachmentId,
  )}/content`;
}

/** OpsRequest を返す fetch を正規化付きで実行する。 @graph-connects none */
function requestOpsRequest(path: string, init?: RequestInit): Promise<OpsRequest> {
  return request<OpsRequest>(path, init).then(normalizeOpsRequest);
}

/**
 * SLO 系エンドポイント（/ops/slo・/ops/slo/state-history）の期間クエリ。両者が同じ期間を指すよう 1 か所で組み立てる。
 * @graph-connects none
 */
function sloPeriodQuery(period: OpsSloGranularity, start?: string): string {
  const query = new URLSearchParams({ period });
  if (start !== undefined) query.set('start', start);
  return query.toString();
}

/**
 * ops API クライアント（一覧・件数）。
 * @graph-stack product-innovation-lab
 * @graph-domain Operations
 * @graph-business ops 依頼の一覧取得（offset ページング）・ステート別件数・単一依頼取得のメソッド群
 * @graph-connects /ops/requests [calls] ops 依頼一覧の取得
 * @graph-connects /ops/requests/status-counts [calls] ステート別件数の取得
 * @graph-connects /ops/requests/{id} [calls] 単一依頼の取得
 * @graph-connects /ops/requests/{id}/assignee [calls] 単一依頼の担当者変更（PATCH）
 * @graph-connects /ops/requests/{id}/urgency [calls] 単一依頼の緊急度変更（PATCH、起票者-or-担当者）
 * @graph-connects /ops/requests/{id}/escalate [calls] 単一依頼のエスカレーション（POST、escalated=true + urgency 即時）
 * @graph-connects /ops/requests/{id}/deescalate [calls] 単一依頼のエスカレーション解除（POST、escalated=false の undo。urgency は変更しない）
 * @graph-connects /ops/requests/{id}/send-back [calls] 単一依頼を依頼者へ差戻し（POST、state=blocked + 起票スレッドへ差戻し理由 + メッセージ返信）
 * @graph-connects /ops/requests/{id}/notes [calls] 単一依頼への調査メモ追記（POST、notes タイムラインに 1 件追加）
 * @graph-connects /ops/requests/{id}/memo [calls] 単一依頼の調査メモ（memo スカラー）編集（PATCH、Firestore 発番のみ）
 * @graph-connects /ops/requests/{id}/point [calls] 単一依頼の工数ポイント設定（PATCH、0–21 / null。0=対応不要、Firestore 発番のみ）
 * @graph-connects /ops/requests/{id}/translate [calls] 単一依頼の依頼内容の翻訳（POST）
 * @graph-connects /ops/slo [calls] SLO ダッシュボード用の期間内依頼の取得
 * @graph-connects /ops/slo/state-history [calls] 内訳タブの差戻し統計用の期間内ステート履歴の取得
 * @graph-connects /ops/similar [calls] 類似 ops + 既存バグチケットの重複スキャン
 * @graph-connects /ops/requests [calls] 新規 ops 依頼の作成（POST。preScan / linkedTickets / 補足対応元 followUpOf を任意で同送）
 * @graph-connects /ops/requests/{id}/content [calls] 依頼内容（背景 / 対応依頼 / WFID / CS URL）の部分更新（PATCH、Firestore 発番のみ）
 * @graph-connects /ops/requests/{id}/related-tickets [calls] 関連チケットの紐付け（POST）・解除（DELETE、target/ref クエリ）
 * @graph-connects /ops/requests/{id}/attachments [calls] 添付ファイルの追加（POST、multipart。field=file。追記後の依頼を返す）
 * @graph-connects /ops/requests/{id}/attachments/{attachmentId} [calls] 添付ファイルの削除（DELETE、削除後の依頼を返す）
 * @graph-connects /ops/assignments [calls] サービス別担当割当の取得（GET）・更新（PUT）
 * @graph-connects /ops/assignments/history [calls] ロスター変更履歴（実績）の取得
 */
export const opsApi = {
  /** 絞り込み + offset ページングで ops 依頼一覧を取得する。 */
  listRequests: (params: {
    status: OpsStatusFilter;
    service: OpsServiceFilter;
    offset: number;
    limit: number;
    requester?: string;
    assignee?: string;
    userId?: string;
    q?: string;
    /** 対象範囲（requests = 依頼者のいる依頼 / system = システムオペレーション）。未指定はサーバー既定の requests。 */
    scope?: OpsListScope;
  }): Promise<OpsRequestsPage> => {
    const query = new URLSearchParams({
      status: params.status,
      service: params.service,
      offset: String(params.offset),
      limit: String(params.limit),
    });
    if (params.requester !== undefined) query.set('requester', params.requester);
    if (params.assignee !== undefined) query.set('assignee', params.assignee);
    if (params.userId !== undefined) query.set('userId', params.userId);
    if (params.q !== undefined && params.q !== '') query.set('q', params.q);
    if (params.scope !== undefined) query.set('scope', params.scope);
    return request<OpsRequestsPage>(`/ops/requests?${query.toString()}`).then((page) => ({
      ...page,
      // 1 レスポンスは Firestore ブロック + BQ ミラーブロックの連結。両方に載った依頼が id 重複のまま
      // 届くと、どの consumer が畳んでも `key={id}` の一覧で React の再描画が壊れる。受け取る境界で
      // 一意にして、呼び出し側の畳み忘れに依存しない（total はサーバー値のまま。上記 dedupe の JSDoc 参照）。
      items: dedupeOpsRequestsById(page.items.map(normalizeOpsRequest)),
    }));
  },
  /** サービス絞り込み + フリーワード検索（q）下でのステート別件数を取得する。 */
  statusCounts: (
    service: OpsServiceFilter,
    q?: string,
    scope?: OpsListScope,
  ): Promise<OpsStatusCounts> => {
    const query = new URLSearchParams({ service });
    if (q !== undefined && q !== '') query.set('q', q);
    // 未指定はサーバー既定（requests）に任せる。範囲の既定値を入口に写さない。
    if (scope !== undefined) query.set('scope', scope);
    return request<OpsStatusCounts>(`/ops/requests/status-counts?${query.toString()}`);
  },
  /** id（slug:No）で単一依頼を取得する。404（未登録 id / 番号なし依頼）は null を返す。 */
  getRequest: async (id: string): Promise<OpsRequest | null> => {
    try {
      return normalizeOpsRequest(
        await request<OpsRequest>(`/ops/requests/${encodeURIComponent(id)}`),
      );
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  /** 単一 ops 依頼の担当者を変更する（PATCH /ops/requests/{id}/assignee）。null = 未割当に戻す。 */
  setRequestAssignee: (id: string, assigneeName: string | null): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/assignee`, {
      method: 'PATCH',
      body: JSON.stringify({ assigneeName }),
    }),
  /**
   * 単一 ops 依頼の進捗ステートを変更する（PATCH /ops/requests/{id}/state）。closed = 解決済みにする。
   * closeReport を渡すと（closed のとき）承認済みの対応結論として保存し、完了通知に添える。
   */
  setRequestState: (
    id: string,
    state: OpsState,
    closeReport?: string | null,
  ): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/state`, {
      method: 'PATCH',
      body: JSON.stringify(closeReport === undefined ? { state } : { state, closeReport }),
    }),
  /**
   * ops 依頼の対応結論の下書きを AI 生成する（POST /ops/requests/{id}/close-report/draft）。
   * 調査メモ（memo）と調査ログ（notes）が両方空なら draft は空文字（呼び出し側が人手入力を促す）。
   * 送信前・人手レビュー用で Slack へは投稿しない。
   */
  draftCloseReport: (id: string): Promise<{ draft: string }> =>
    request<{ draft: string }>(`/ops/requests/${encodeURIComponent(id)}/close-report/draft`, {
      method: 'POST',
    }),
  /**
   * 単一 ops 依頼の緊急度を変更する（PATCH /ops/requests/{id}/urgency）。通常 / 当日 / 即時。
   * urgency は escalated とは独立した純粋な優先度で、この経路では escalated / CTO 通知は発火しない。Slack 通知もしない。起票者または担当者のみ許可（それ以外は 403）。
   */
  setRequestUrgency: (id: string, urgency: OpsUrgency): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/urgency`, {
      method: 'PATCH',
      body: JSON.stringify({ urgency }),
    }),
  /**
   * 単一 ops 依頼をエスカレーションする（POST /ops/requests/{id}/escalate、body なし）。
   * escalated を立て #product_all へ通知し、利便性で urgency も即時へ引き上げる。担当エンジニアのみ許可（それ以外は 403）。
   */
  escalateRequest: (id: string): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/escalate`, { method: 'POST' }),
  /**
   * 単一 ops 依頼のエスカレーションを解除する（POST /ops/requests/{id}/deescalate、body なし）。
   * escalated を false へ戻す undo（urgency には触れない）。エスカレーション通知スレッドへ解除を返信する（best-effort）。
   * 担当エンジニアのみ許可（それ以外は 403）。
   */
  deescalateRequest: (id: string): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/deescalate`, { method: 'POST' }),
  /**
   * 単一 ops 依頼を依頼者へ差戻す（POST /ops/requests/{id}/send-back）。reason（集計用の差戻し理由）と message を
   * 依頼へ記録し、state を blocked（差戻し）にして起票スレッドへ差戻し理由のラベルと message（人手が編集・承認したテキスト）を返信する。Firestore 発番のみ（legacy は 404）。
   */
  sendBack: (id: string, reason: OpsSendBackReason, message: string): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/send-back`, {
      method: 'POST',
      body: JSON.stringify({ reason, message }),
    }),
  /**
   * 単一 ops 依頼の notes タイムラインに調査メモを 1 件追記する（POST /ops/requests/{id}/notes）。
   * author はサーバーが認証セッションから付与するため、クライアントは text だけを送る。成功で
   * 追記後の依頼（notes に新規メモを含む）を返す。
   */
  addNote: (id: string, text: string): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/notes`, {
      method: 'POST',
      body: JSON.stringify({ text }),
    }),
  /**
   * 単一 ops 依頼の調査メモ（memo スカラー）を編集する（PATCH /ops/requests/{id}/memo）。
   * null / 空文字で消去。Firestore 発番（No >= 10,000）の依頼のみ対象で legacy は 404。成功で更新後の依頼を返す。
   */
  setMemo: (id: string, memo: string | null): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/memo`, {
      method: 'PATCH',
      body: JSON.stringify({ memo }),
    }),
  /**
   * 単一 ops 依頼の工数ポイント（0–21 の整数、0=対応不要、null で見積もり待ち）を設定する
   * （PATCH /ops/requests/{id}/point）。Firestore 発番（No >= 10,000）の依頼のみ対象で legacy は 404、
   * 範囲外は 400。成功で更新後の依頼を返す（Backlog 反映は reconciler が別途行う）。
   */
  setPoint: (id: string, point: number | null): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/point`, {
      method: 'PATCH',
      body: JSON.stringify({ point }),
    }),
  /**
   * 単一 ops 依頼の作業時間（分。null で未記録へ戻す）を設定する
   * （PATCH /ops/requests/{id}/work-minutes）。Firestore 発番の依頼のみ対象（legacy は 404）。
   */
  setWorkMinutes: (id: string, workMinutes: number | null): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/work-minutes`, {
      method: 'PATCH',
      body: JSON.stringify({ workMinutes }),
    }),
  /**
   * 単一 ops 依頼の依頼内容（背景 + 対応依頼 + 調査メモ + notes タイムライン）を target 言語
   * （ja / en / vi）へ翻訳する（POST /ops/requests/{id}/translate）。原文は書き換えない read-only 操作。
   * notes は request.notes と同じ順（createdAt 昇順）の訳文配列で返る。
   */
  translateRequest: (
    id: string,
    target: 'ja' | 'en' | 'vi',
  ): Promise<{ background: string; requested: string; memo: string; notes: string[] }> =>
    request<{ background: string; requested: string; memo: string; notes: string[] }>(
      `/ops/requests/${encodeURIComponent(id)}/translate`,
      { method: 'POST', body: JSON.stringify({ target }) },
    ),
  /**
   * SLO ダッシュボード用に、指定期間（起票日基準）に起票された依頼を全件取得する。
   * start（YYYY-MM-DD、月初 / 四半期初）省略時は当月 / 当四半期、指定時はその月 / 四半期。
   */
  getSlo: (period: OpsSloGranularity, start?: string): Promise<{ requests: OpsRequest[] }> =>
    request<{ requests: OpsRequest[] }>(`/ops/slo?${sloPeriodQuery(period, start)}`).then((r) => ({
      requests: r.requests.map(normalizeOpsRequest),
    })),
  /**
   * 内訳タブ（サービス別の差戻し）用に、getSlo と同じ期間に起票された native 依頼のステート履歴を取得する。
   * 差戻しの復元・集計は呼び出し側が ops-core（aggregateOpsSendBacksByService）で行う。
   */
  getSloStateHistory: (
    period: OpsSloGranularity,
    start?: string,
  ): Promise<{ events: OpsStateHistoryEvent[] }> =>
    request<{ events: OpsStateHistoryEvent[] }>(
      `/ops/slo/state-history?${sloPeriodQuery(period, start)}`,
    ),
  /** SLO ボリューム推移チャートの元データ（直近 N ヶ月に起票された依頼）を取得する。 */
  getSloTrend: (months: OpsSloTrendMonths): Promise<{ requests: OpsRequest[] }> => {
    const query = new URLSearchParams({ months: String(months) });
    return request<{ requests: OpsRequest[] }>(`/ops/slo/trend?${query.toString()}`).then((r) => ({
      requests: r.requests.map(normalizeOpsRequest),
    }));
  },
  /** 依頼本文から内容が類似する ops 依頼 + 既存バグチケットを取得する（新規起票前の重複スキャン）。 */
  getSimilar: (text: string): Promise<OpsSimilarResult> => {
    const query = new URLSearchParams({ text });
    return request<OpsSimilarResult>(`/ops/similar?${query.toString()}`);
  },
  /**
   * 新規 ops 依頼を作成する（POST /ops/requests）。requester はサーバーが認証セッションから付与する。
   * 対象ユーザーは draft の userIds（配列 or null=N/A）で送る。extras で起票時の事前スキャン結果（preScan）と
   * 選択した初期関連チケット（linkedTickets）も任意で送れる。
   */
  createRequest: (
    draft: OpsRequestDraft,
    extras: OpsCreateRequestExtras = {},
  ): Promise<OpsRequest> =>
    requestOpsRequest('/ops/requests', {
      method: 'POST',
      body: JSON.stringify({ ...draft, ...extras }),
    }),
  /**
   * ops 依頼の依頼内容（背景 / 対応依頼 / WFID / CS チケット URL）を部分更新する（PATCH /content）。
   * 指定したフィールドのみ更新し、wfid / csTicketUrl は null / 空文字で消去する。Firestore 発番の依頼のみ
   * （legacy は 404）。成功で更新後の依頼を返す。
   */
  editContent: (id: string, edit: OpsContentEdit): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/content`, {
      method: 'PATCH',
      body: JSON.stringify(edit),
    }),
  /**
   * ops 依頼へ関連チケットを 1 件紐付ける（POST /related-tickets）。(target, ref) が同じ既存要素は置き換える
   * （重複排除つき upsert）。linkedBy / linkedAt はサーバーが付与する。Firestore 発番の依頼のみ（legacy は 404）。
   * 成功で紐付け後の依頼を返す。
   */
  addRelatedTicket: (id: string, input: OpsRelatedTicketInput): Promise<OpsRequest> =>
    requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/related-tickets`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
  /**
   * ops 依頼から (target, ref) の関連チケットを外す（DELETE /related-tickets?target&ref）。参照キーはクエリで
   * 受ける。Firestore 発番の依頼のみ（legacy は 404）。成功で解除後の依頼を返す。
   */
  removeRelatedTicket: (
    id: string,
    key: { target: 'ops' | 'backlog'; ref: string },
  ): Promise<OpsRequest> => {
    const query = new URLSearchParams({ target: key.target, ref: key.ref });
    return requestOpsRequest(
      `/ops/requests/${encodeURIComponent(id)}/related-tickets?${query.toString()}`,
      { method: 'DELETE' },
    );
  },
  /**
   * ops 依頼へ添付ファイルを 1 件アップロードする（POST /attachments、multipart/form-data。ファイルは
   * form フィールド `file`）。uploadedBy はサーバーが認証セッションから付与する。成功で追記後の依頼
   * （attachments に新規添付を含む）を返す。Content-Type はブラウザが boundary 付きで自動設定する。
   */
  uploadAttachment: (id: string, file: File): Promise<OpsRequest> => {
    const form = new FormData();
    form.append('file', file);
    return requestOpsRequest(`/ops/requests/${encodeURIComponent(id)}/attachments`, {
      method: 'POST',
      body: form,
    });
  },
  /**
   * ops 依頼から添付ファイルを 1 件削除する（DELETE /attachments/{attachmentId}）。サーバー側で
   * アップロード者 or admin のみ許可（それ以外は 403）。成功で削除後の依頼を返す。
   */
  deleteAttachment: (id: string, attachmentId: string): Promise<OpsRequest> =>
    requestOpsRequest(
      `/ops/requests/${encodeURIComponent(id)}/attachments/${encodeURIComponent(attachmentId)}`,
      { method: 'DELETE' },
    ),
  /** 全サービスの担当割当を取得する（GET /ops/assignments）。 */
  listAssignments: (): Promise<OpsAssignment[]> =>
    request<{ assignments: OpsAssignment[] }>('/ops/assignments').then((r) => r.assignments),
  /** 1 サービスの担当割当を更新する（PUT /ops/assignments/{service}）。 */
  setAssignment: (service: OpsService, edit: OpsAssignmentEdit): Promise<OpsAssignment> =>
    request<OpsAssignment>(`/ops/assignments/${service}`, {
      method: 'PUT',
      body: JSON.stringify(edit),
    }),
  /** 記録済みロスター変更履歴（実績。新しい順）を取得する（GET /ops/assignments/history）。 */
  listAssignmentHistory: (): Promise<OpsAssignmentEvent[]> =>
    request<{ events: OpsAssignmentEvent[] }>('/ops/assignments/history').then((r) => r.events),
  /**
   * ops 依頼からバグチケット下書きを AI 生成する（POST /ops/requests/{id}/bug-ticket/draft）。
   * Backlog には書き込まない（起票前レビュー用）。similarBugKeys は起因案件候補として渡す。
   */
  draftBugTicket: (id: string, similarBugKeys: string[]): Promise<OpsBugTicketDraft> =>
    request<OpsBugTicketDraft>(`/ops/requests/${encodeURIComponent(id)}/bug-ticket/draft`, {
      method: 'POST',
      body: JSON.stringify({ similarBugKeys }),
    }),
  /**
   * 人手確認後の下書きで Backlog にバグチケットを起票する（POST /ops/requests/{id}/bug-ticket）。
   * 成功で依頼に URL が紐付く。二重起票は 400。
   */
  createBugTicket: (id: string, input: CreateOpsBugTicketInput): Promise<CreatedOpsBugTicket> =>
    request<CreatedOpsBugTicket>(`/ops/requests/${encodeURIComponent(id)}/bug-ticket`, {
      method: 'POST',
      body: JSON.stringify(input),
    }),
};
